/*
|--------------------------------------------------------------------------
| OpenAPI Schema Service
|--------------------------------------------------------------------------
|
| Service class responsible for generating OpenAPI schema objects from
| resourceful model metadata. Extracts OpenAPI-related logic from the
| mixin to improve code organization and maintainability.
|
| This service handles:
| - Data type extraction from resourceful data types
| - Column schema generation
| - Computed accessor schema generation  
| - Relationship schema generation
| - Complete model schema generation
*/

import { deepmerge } from 'deepmerge-ts'
import { isResourcefulModel } from './type_guards'
import { encode } from '@nhtio/lucid-resourceful/encoding'
import { isString, isObject, stripUndefinedValuesFromObject } from './utils'
import { resolveModelDisplayColumn } from './router_macro/utils/relationships'
import type { OpenAPIV3 } from 'openapi-types'
import type { ResourcefulModel } from './mixin'
import type { ResourcefulEnumObject, ResourcefulEnum } from './data_types'
import type {
  ApplicationService,
  HttpContext,
  ResourcefulDataType,
} from '@nhtio/lucid-resourceful/types'
import type {
  RelationshipsContract,
  HasOneRelationContract,
  BelongsToRelationContract,
  HasManyRelationContract,
  HasManyThroughRelationContract,
  ManyToManyRelationContract,
} from '@adonisjs/lucid/types/relations'
import type {
  ResourcefulModelColumnMetaSchema,
  ResourcefulModelComputedAccessorMetaSchema,
  ResourcefulModelRelationshipMetaSchema,
  ResourcefulModelMetaSchema,
  ResourcefulModelOpenApiSchema,
  ResourcefulModelOpenApiRelatedSchema,
  ResourcefulModelRelationshipMeta,
  ResourcefulModelFieldRelationshipMeta,
} from './types'

export interface GenerateModelSchemaOptions {
  includeRelations?: boolean
  relationsDeepLevel?: number | null
  ctx?: HttpContext
  app?: ApplicationService
}

export interface NormalizedGenerateModelSchemaOptions extends GenerateModelSchemaOptions {
  includeRelations: boolean
  relationsDeepLevel: number | null
  ctx?: HttpContext
  app?: ApplicationService
}

/**
 * Interface for data type information extracted from resourceful data types.
 * Contains the OpenAPI type and optional format information.
 */
interface DataTypeInfo {
  /** The OpenAPI data type (string, number, integer, boolean, object, array) */
  type: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array'
  /** Optional format specification (date, date-time, binary, int32, int64, float, double) */
  format?: string
}

/**
 * Interface for Lucid model definitions that may have serializeAs property.
 */
interface LucidDefinitionWithSerializeAs {
  serializeAs?: string | null
  autoCreate?: boolean
}

/**
 * Interface for data type instances with validation constraints.
 */
interface DataTypeWithConstraints {
  type: string
  format?: string
  items?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  multipleOf?: number
  maximum?: number
  exclusiveMaximum?: boolean
  minimum?: number
  exclusiveMinimum?: boolean
  maxLength?: number
  minLength?: number
  pattern?: string
  additionalProperties?: boolean | OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  maxProperties?: number
  minProperties?: number
  properties?: Record<string, OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject>
  maxItems?: number
  minItems?: number
  uniqueItems?: boolean
  allOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  oneOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  anyOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  not?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  required?: string[]
  enum?: unknown[]
  nullable?: boolean
}

/**
 * Interface for property definitions with validation constraints.
 */
interface PropertyDefinitionWithConstraints {
  type: ResourcefulDataType
  description?: string
  nullable?: boolean
  externalDocs?: OpenAPIV3.ExternalDocumentationObject
  example?: unknown
  items?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  multipleOf?: number
  maximum?: number
  exclusiveMaximum?: boolean
  minimum?: number
  exclusiveMinimum?: boolean
  maxLength?: number
  minLength?: number
  pattern?: string
  additionalProperties?: boolean | OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  maxProperties?: number
  minProperties?: number
  properties?: Record<string, OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject>
  maxItems?: number
  minItems?: number
  uniqueItems?: boolean
  allOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  oneOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  anyOf?: (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]
  not?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject
  required?: string[]
  enum?: unknown[]
}

/**
 * Service class for generating OpenAPI schema objects from resourceful model metadata.
 *
 * This service encapsulates all OpenAPI-related functionality that was previously
 * embedded in the resourceful mixin, providing better code organization and
 * easier testing. It handles the extraction of data type information from
 * resourceful data types and generates proper OpenAPI schema objects with
 * vendor extensions for field capabilities (filterable, sortable, aggregatable).
 *
 * @example
 * ```typescript
 * const service = new OpenApiSchemaService()
 * const schema = await service.generateModelSchema('read', metaSchema, model)
 * ```
 */
export class OpenApiSchemaService {
  /**
   * Gets the field key for a property, handling serializeAs transformation.
   *
   * This private method extracts the appropriate field name for a property,
   * taking into account the serializeAs option from Lucid model definitions.
   * Returns null if the field cannot be read (following Lucid's serializeAs behavior).
   *
   * @param key - The original property key
   * @param definition - The Lucid model definition (column, computed, or relation)
   * @returns The field key to use, the original key if no transformation is needed, or null if field cannot be read
   */
  #getFieldKey(key: string, definition: LucidDefinitionWithSerializeAs): string | null {
    if (definition.serializeAs === null) {
      return null
    }
    /* istanbul ignore if -- @preserve */
    if (isString(definition.serializeAs)) {
      return definition.serializeAs
    }
    /* istanbul ignore next -- @preserve */
    return key
  }

  /**
   * Extracts OpenAPI type and format information from resourceful data types.
   *
   * This private method handles the mapping from resourceful data type instances
   * to their corresponding OpenAPI type and format specifications. It properly
   * handles all supported resourceful data types and provides appropriate
   * fallback behavior for unknown types.
   *
   * @param dataType - The resourceful data type instance to extract information from
   * @returns Object containing the OpenAPI type and optional format
   *
   * @example
   * ```typescript
   * const info = this.#extractDataTypeInfo(ResourcefulStringType())
   * // Returns: { type: 'string' }
   *
   * const info = this.#extractDataTypeInfo(ResourcefulDateTimeType())
   * // Returns: { type: 'string', format: 'date-time' }
   * ```
   */
  #extractDataTypeInfo(dataType: ResourcefulDataType): DataTypeInfo {
    // Extract the base type from the data type instance with error handling
    let baseType: string
    try {
      baseType = dataType.type
    } catch (error) {
      // Fallback to string type if type extraction fails
      /* istanbul ignore next -- @preserve */
      return { type: 'string' }
    }

    // Extract format information if available
    const format =
      'format' in dataType && typeof dataType.format === 'string' ? dataType.format : undefined

    // Handle specific data type mappings based on the type and format
    /* istanbul ignore next -- @preserve */
    switch (baseType) {
      case 'string': {
        // String types may have specific formats
        if (format) {
          return { type: 'string', format }
        }
        return { type: 'string' }
      }

      case 'number': {
        // Number types may have float/double format
        if (format === 'float' || format === 'double') {
          return { type: 'number', format }
        }
        return { type: 'number' }
      }

      case 'integer': {
        // Integer types may have int32/int64 format
        if (format === 'int32' || format === 'int64') {
          return { type: 'integer', format }
        }
        return { type: 'integer' }
      }

      case 'boolean': {
        return { type: 'boolean' }
      }

      case 'object': {
        return { type: 'object' }
      }

      case 'array': {
        return { type: 'array' }
      }

      default: {
        /* istanbul ignore next -- @preserve */
        return { type: 'string' }
      }
    }
  }

  #resolveEnums<A = any>(
    fromDefinition?: ResourcefulEnum<A>,
    fromDataType?: ResourcefulEnum<A>
  ): A[] | undefined {
    if (Array.isArray(fromDefinition)) {
      return fromDefinition.map((v) => (isObject(v) ? v.value : v))
    } else if (Array.isArray(fromDataType)) {
      return fromDataType.map((v) => (isObject(v) ? v.value : v))
    }
    return undefined
  }

  #resolveChoices<A = any>(
    fromDefinition?: ResourcefulEnum<A>,
    fromDataType?: ResourcefulEnum<A>
  ): ResourcefulEnumObject<A>[] | undefined {
    if (Array.isArray(fromDefinition)) {
      return fromDefinition.map((v) =>
        isObject(v)
          ? ({
              title: v.title || String(v.value),
              value: v.value,
              i18n: v.i18n || false,
            } satisfies ResourcefulEnumObject<A>)
          : ({
              title: String(v),
              value: v,
              i18n: false,
            } satisfies ResourcefulEnumObject<A>)
      )
    } else if (Array.isArray(fromDataType)) {
      return fromDataType.map((v) =>
        isObject(v)
          ? ({
              title: v.title || String(v.value),
              value: v.value,
              i18n: v.i18n || false,
            } satisfies ResourcefulEnumObject<A>)
          : ({
              title: String(v),
              value: v,
              i18n: false,
            } satisfies ResourcefulEnumObject<A>)
      )
    }
    return undefined
  }

  /**
   * Generates a complete OpenAPI schema object for a resourceful model.
   *
   * This method creates a comprehensive OpenAPI v3 schema representation of the model
   * by processing the provided metadata schema. The metadata should already have
   * field-level access control permissions evaluated for the given request context.
   * The generated schema includes vendor extensions that provide metadata about field
   * capabilities for API consumers:
   * - `x-resourceful-filterable`: Array of field names that can be used in filter operations
   * - `x-resourceful-sortable`: Array of field names that can be used in sort operations
   * - `x-resourceful-aggregatable`: Array of field names that can be used in aggregation operations
   *
   * @param operation - The operation type ('read' or 'write') to determine which fields to include
   * @param resourcefulModelMetaSchema - The resourceful model metadata with ACL filtering applied
   * @param model - Optional model class for additional context
   * @returns Complete OpenAPI schema object with field capability metadata
   *
   * @example
   * ```typescript
   * const service = new OpenApiSchemaService()
   * const schema = service.generateModelSchema('read', metaSchema, UserModel)
   * // Returns OpenAPI schema with vendor extensions:
   * // {
   * //   type: 'object',
   * //   properties: { ... },
   * //   'x-resourceful-filterable': ['name', 'email', 'user.name'],
   * //   'x-resourceful-sortable': ['name', 'createdAt'],
   * //   'x-resourceful-aggregatable': ['totalSales', 'orderCount']
   * // }
   * ```
   */
  /* istanbul ignore next -- @preserve */
  async generateModelSchema(
    /* istanbul ignore next -- @preserve */
    operation: 'read' | 'write' = 'read',
    resourcefulModelMetaSchema: ResourcefulModelMetaSchema,
    options: NormalizedGenerateModelSchemaOptions,
    model?: ResourcefulModel
  ): Promise<ResourcefulModelOpenApiSchema> {
    if (
      !isObject(resourcefulModelMetaSchema) ||
      !resourcefulModelMetaSchema.name ||
      !isResourcefulModel(model)
    ) {
      return {
        type: 'object',
        properties: {},
        required: [],
      }
    }
    // Initialize the base schema structure
    const schema: ResourcefulModelOpenApiSchema = {
      'type': 'object',
      'title': resourcefulModelMetaSchema.name,
      'description': resourcefulModelMetaSchema.description,
      'externalDocs': resourcefulModelMetaSchema.externalDocs,
      'example': resourcefulModelMetaSchema.example,
      'properties': {},
      'x-resourceful-filterable': {},
      'x-resourceful-sortable': [],
      'x-resourceful-aggregatable': [],
      'x-resourceful-auto-create': [],
    }

    // Process each property in the metadata schema
    /* istanbul ignore next -- @preserve */
    Object.entries(resourcefulModelMetaSchema.properties).forEach(
      async ([propertyKey, propertyMeta]) => {
        // Get the field key (handles serializeAs transformation)
        let resolvedColumnName = this.#getFieldKey(
          propertyKey,
          propertyMeta.lucidDefinitions as LucidDefinitionWithSerializeAs
        )

        if (null === resolvedColumnName && 'read' === operation) {
          // If the field key is null, that means that the field cannot be "read"
          // This is part of the baseline lucid decorator API, so we need to honor the
          // expected behavior
          return
        } else if (null === resolvedColumnName && 'write' === operation) {
          resolvedColumnName = propertyKey
        }

        // Skip properties that cannot be accessed for the given operation
        if (
          ('read' === operation && propertyMeta.canRead === false) ||
          ('write' === operation && propertyMeta.canWrite === false)
        )
          return

        // Generate schema based on property type
        switch (propertyMeta.kind) {
          case 'column': {
            schema.properties![propertyKey] = this.generateColumnSchema(
              model,
              propertyKey,
              propertyMeta as ResourcefulModelColumnMetaSchema
            )
            if (propertyMeta.filterable) {
              schema['x-resourceful-filterable']![propertyKey] = schema.properties![propertyKey]
            }
            if (propertyMeta.sortable && Array.isArray(schema['x-resourceful-sortable'])) {
              schema['x-resourceful-sortable'].push(propertyKey)
            }
            if (propertyMeta.aggregatable && Array.isArray(schema['x-resourceful-aggregatable'])) {
              schema['x-resourceful-aggregatable'].push(propertyKey)
            }
            if (
              propertyMeta.definition &&
              'autoCreate' in propertyMeta.definition &&
              true === propertyMeta.definition.autoCreate &&
              Array.isArray(schema['x-resourceful-auto-create'])
            ) {
              schema['x-resourceful-auto-create'].push(propertyKey)
            }
            return
          }

          case 'computedAccessor': {
            schema.properties![propertyKey] = this.generateComputedAccessorSchema(
              propertyKey,
              propertyMeta as ResourcefulModelComputedAccessorMetaSchema
            )
            return
          }

          case 'relationship': {
            const relatedSchema = await this.generateRelationshipSchema(
              propertyKey,
              propertyMeta as ResourcefulModelRelationshipMetaSchema,
              operation,
              options
            )
            if (relatedSchema) {
              const lucidDefinition = model.$relationsDefinitions.get(propertyKey)
              if (lucidDefinition && 'read' === operation) {
                switch (lucidDefinition.type) {
                  case 'hasOne':
                  case 'belongsTo':
                    schema.properties![propertyKey] = relatedSchema
                    break
                }
              }
            }
            if (isObject(propertyMeta.filterable)) {
              const fixedFilterable: { [key: string]: any } = {}
              Object.entries(propertyMeta.filterable).forEach(([key, value]) => {
                // Use dot notation for related fields
                fixedFilterable[`${resolvedColumnName}.${key}`] = value
              })
              schema['x-resourceful-filterable'] = deepmerge(
                schema['x-resourceful-filterable'],
                fixedFilterable
              )
            }
            return
          }
        }
      }
    )

    // Build the required fields array based on non-nullable properties
    const requiredPropSet: Set<string> = new Set()
    Object.entries(schema.properties!).forEach(([propertyKey, propertySchema]) => {
      // Skip reference objects (relationships)
      if (!isObject(propertySchema) || '$ref' in propertySchema) {
        return
      }

      // Check if the property is nullable
      // Properties are required if:
      // 1. nullable is false (explicitly set)
      // 2. nullable is undefined (not specified, defaults to non-nullable)
      // Properties are NOT required if:
      // 1. nullable is true (explicitly nullable)
      const isNullable = propertySchema.nullable === true
      // If not explicitly nullable, it should be required
      if (
        !isNullable &&
        ('read' === operation ||
          (!model.$resourcefulComputedAccessors.has(propertyKey) &&
            !model.$resourcefulRelationships.has(propertyKey) &&
            (!schema['x-resourceful-auto-create'] ||
              !schema['x-resourceful-auto-create'].includes(propertyKey))))
      ) {
        requiredPropSet.add(propertyKey)
      }
    })
    schema.required = Array.from(requiredPropSet)

    return schema
  }

  #makeRelationshipChoicesUrl(model: ResourcefulModel, relationship: string): string {
    const encodable = {
      ResourcefulModel: model.name,
      relationship,
    }
    return `#/$meta.choices/${encodeURIComponent(encode(encodable))}`
  }

  #extractSingleRelationshipMeta(
    model: ResourcefulModel,
    propertyKey: string,
    _propertyMeta: ResourcefulModelColumnMetaSchema,
    relationshipKey: string,
    relationship: HasOneRelationContract<any, any> | BelongsToRelationContract<any, any>
  ): ResourcefulModelFieldRelationshipMeta {
    if (relationship.foreignKey === propertyKey) {
      const resourcefulModelRelationshipMeta = model.$resourcefulRelationships.get(relationshipKey)
      if (!resourcefulModelRelationshipMeta) {
        return false
      }
      const relatedModel = resourcefulModelRelationshipMeta.relatedModel()
      if (!isResourcefulModel(relatedModel)) {
        return false
      }
      const fieldKey = this.#getFieldKey(
        relationshipKey,
        resourcefulModelRelationshipMeta as LucidDefinitionWithSerializeAs
      )
      return {
        title: fieldKey || relationshipKey,
        multiple: false,
        choices: this.#makeRelationshipChoicesUrl(model, relationshipKey),
      }
    }
    return false
  }

  /*
  |--------------------------------------------------------------------------
  | Functionally useless
  |--------------------------------------------------------------------------
  |
  | This method has been deactivated because it is functionally useless. It is
  | not possible for a form to render the choices for a many relationship
  | in a way which is will be processed by the API. HasMany and HasManyThrough
  | relationships need to be managed via a separate form which specifically
  | handles syncronizing relationships.
  |
  | The code is being left as a reference for if and when it does have a use
  | case.
  | 
  */
  // @ts-ignore
  #extractManyRelationshipMeta(
    model: ResourcefulModel,
    propertyKey: string,
    _propertyMeta: ResourcefulModelColumnMetaSchema,
    relationshipKey: string,
    relationship: HasManyRelationContract<any, any> | HasManyThroughRelationContract<any, any>
  ): ResourcefulModelFieldRelationshipMeta {
    if (
      ('hasMany' === relationship.type && relationship.foreignKey === propertyKey) ||
      ('hasManyThrough' === relationship.type && relationship.throughForeignKey === propertyKey)
    ) {
      const resourcefulModelRelationshipMeta = model.$resourcefulRelationships.get(relationshipKey)
      if (!resourcefulModelRelationshipMeta) {
        return false
      }
      const relatedModel = resourcefulModelRelationshipMeta.relatedModel()
      if (!isResourcefulModel(relatedModel)) {
        return false
      }
      const fieldKey = this.#getFieldKey(
        relationshipKey,
        resourcefulModelRelationshipMeta as LucidDefinitionWithSerializeAs
      )
      return {
        title: fieldKey || relationshipKey,
        multiple: true,
        choices: this.#makeRelationshipChoicesUrl(model, relationshipKey),
      }
    }
    return false
  }

  /*
  |--------------------------------------------------------------------------
  | Functionally useless
  |--------------------------------------------------------------------------
  |
  | This method has been deactivated because it is functionally useless. It is
  | not possible for a form to render the choices for a many to many 
  | relationship in a way which is will be processed by the API. ManyToMany
  | relationships need to be managed via a separate form which specifically
  | handles syncronizing relationships.
  |
  | The code is being left as a reference for if and when it does have a use
  | case.
  | 
  */
  // @ts-ignore
  #extractManyToManyRelationshipMeta(
    model: ResourcefulModel,
    propertyKey: string,
    _propertyMeta: ResourcefulModelColumnMetaSchema,
    relationshipKey: string,
    relationship: ManyToManyRelationContract<any, any>
  ): ResourcefulModelFieldRelationshipMeta {
    if (relationship.pivotForeignKey === propertyKey) {
      const resourcefulModelRelationshipMeta = model.$resourcefulRelationships.get(relationshipKey)
      if (!resourcefulModelRelationshipMeta) {
        return false
      }
      const relatedModel = resourcefulModelRelationshipMeta.relatedModel()
      if (!isResourcefulModel(relatedModel)) {
        return false
      }
      const fieldKey = this.#getFieldKey(
        relationshipKey,
        resourcefulModelRelationshipMeta as LucidDefinitionWithSerializeAs
      )
      return {
        title: fieldKey || relationshipKey,
        multiple: true,
        choices: this.#makeRelationshipChoicesUrl(model, relationshipKey),
      }
    }
    return false
  }

  #extractColumnRelationshipMeta(
    model: ResourcefulModel,
    propertyKey: string,
    propertyMeta: ResourcefulModelColumnMetaSchema
  ): ResourcefulModelFieldRelationshipMeta {
    // iterate through model relations to find if this column is a key to any relation
    const possible: Map<string, ResourcefulModelRelationshipMeta> = new Map()
    const processRelationship = (relation: RelationshipsContract, relationshipKey: string) => {
      relation.boot()
      switch (relation.type) {
        case 'hasOne':
        case 'belongsTo': {
          const metaOrFalse = this.#extractSingleRelationshipMeta(
            model,
            propertyKey,
            propertyMeta,
            relationshipKey,
            relation
          )
          if (metaOrFalse) {
            possible.set(relationshipKey, metaOrFalse)
          }
          break
        }
        /*
        |--------------------------------------------------------------------------
        | Functionality Deactivated
        |--------------------------------------------------------------------------
        |
        | @see comments on the #extractManyRelationshipMeta method
        | 
        */
        // case 'hasMany':
        // case 'hasManyThrough': {
        //   const metaOrFalse = this.#extractManyRelationshipMeta(
        //     model,
        //     propertyKey,
        //     propertyMeta,
        //     relationshipKey,
        //     relation
        //   )
        //   if (metaOrFalse) {
        //     possible.set(relationshipKey, metaOrFalse)
        //   }
        //   break
        // }
        /*
        |--------------------------------------------------------------------------
        | Functionality Deactivated
        |--------------------------------------------------------------------------
        |
        | @see comments on the #extractManyToManyRelationshipMeta method
        | 
        */
        // case 'manyToMany': {
        //   const metaOrFalse = this.#extractManyToManyRelationshipMeta(
        //     model,
        //     propertyKey,
        //     propertyMeta,
        //     relationshipKey,
        //     relation
        //   )
        //   if (metaOrFalse) {
        //     possible.set(relationshipKey, metaOrFalse)
        //   }
        //   break
        // }
      }
    }
    if (
      propertyMeta.definition &&
      'relationshipKey' in propertyMeta.definition &&
      'string' === typeof propertyMeta.definition.relationshipKey &&
      propertyMeta.definition.relationshipKey.trim().length > 0
    ) {
      const relation = model.$relationsDefinitions.get(propertyMeta.definition.relationshipKey)
      if (relation) {
        processRelationship(relation, propertyMeta.definition.relationshipKey)
      }
    } else {
      model.$relationsDefinitions.forEach(processRelationship)
    }
    if (possible.size > 0) {
      // Always return the first found relationship if multiple are found
      if (possible.size > 1) {
        // TODO: provide a warning for the developer that multiple relationships were found
        // for the same column key and that they should specifically define `relationshipKey` in
        // the column definition to avoid ambiguity
      }
      return Array.from(possible.values())[0]
    }
    return false
  }

  /**
   * Generates OpenAPI schema for a column property.
   *
   * This method converts resourceful column metadata into an OpenAPI schema object,
   * properly extracting data type information and preserving all existing schema
   * properties such as validation constraints, nullability, and access modifiers.
   *
   * @param propertyKey - The name of the column property
   * @param propertyMeta - The resourceful metadata for the column
   * @returns OpenAPI schema object for the column
   *
   * @example
   * ```typescript
   * const service = new OpenApiSchemaService()
   * const schema = service.generateColumnSchema('name', columnMeta)
   * // Returns: { type: 'string', minLength: 1, maxLength: 100, ... }
   * ```
   */
  generateColumnSchema(
    model: ResourcefulModel,
    propertyKey: string,
    propertyMeta: ResourcefulModelColumnMetaSchema
  ): ResourcefulModelOpenApiSchema {
    // Extract data type information from the resourceful data type
    if (!isObject(propertyMeta) || !propertyMeta.definition || !propertyMeta.lucidDefinitions) {
      return {}
    }
    const dataTypeInfo = this.#extractDataTypeInfo(propertyMeta.definition.type)
    const relationshipMeta = this.#extractColumnRelationshipMeta(model, propertyKey, propertyMeta)
    // Get the field key (handles serializeAs)
    const fieldKey = this.#getFieldKey(
      propertyKey,
      propertyMeta.lucidDefinitions as LucidDefinitionWithSerializeAs
    )

    // Extract validation constraints from the data type instance and definition
    const dataType = propertyMeta.definition.type as DataTypeWithConstraints
    const definition = propertyMeta.definition as PropertyDefinitionWithConstraints

    // Build the schema object with proper type information and all existing properties
    /* istanbul ignore next -- @preserve */
    const schemaProperties = stripUndefinedValuesFromObject({
      // Core OpenAPI type information (properly extracted)
      type: dataTypeInfo.type,
      format: dataTypeInfo.format,

      // Schema metadata
      items: definition.items || dataType.items,
      title: fieldKey || propertyKey,
      description: definition.description,
      default:
        'default' in definition
          ? definition.default
          : this.getDefaultValueFromPropertyMeta(propertyMeta),

      // Numeric constraints (from data type or definition)
      multipleOf: definition.multipleOf || dataType.multipleOf,
      maximum: definition.maximum || dataType.maximum,
      exclusiveMaximum: definition.exclusiveMaximum || dataType.exclusiveMaximum,
      minimum: definition.minimum || dataType.minimum,
      exclusiveMinimum: definition.exclusiveMinimum || dataType.exclusiveMinimum,

      // String constraints (from data type or definition)
      maxLength: definition.maxLength || dataType.maxLength,
      minLength: definition.minLength || dataType.minLength,
      pattern: definition.pattern || dataType.pattern,

      // Object constraints (from data type or definition)
      additionalProperties: definition.additionalProperties || dataType.additionalProperties,
      maxProperties: definition.maxProperties || dataType.maxProperties,
      minProperties: definition.minProperties || dataType.minProperties,
      properties: definition.properties || dataType.properties,

      // Array constraints (from data type or definition)
      maxItems: definition.maxItems || dataType.maxItems,
      minItems: definition.minItems || dataType.minItems,
      uniqueItems: definition.uniqueItems || dataType.uniqueItems,

      // Schema composition (from data type or definition)
      allOf: definition.allOf || dataType.allOf,
      oneOf: definition.oneOf || dataType.oneOf,
      anyOf: definition.anyOf || dataType.anyOf,
      not: definition.not || dataType.not,

      // Validation and constraints (from data type or definition)
      required: definition.required || dataType.required,
      enum: this.#resolveEnums(definition.enum, dataType.enum),
      nullable:
        definition.nullable !== undefined ? definition.nullable : dataType.nullable || false,

      // Access control
      readOnly: propertyMeta.canRead && !propertyMeta.canWrite,
      writeOnly: !propertyMeta.canRead && propertyMeta.canWrite,

      // Documentation
      externalDocs: definition.externalDocs,
      example: definition.example,

      // Form Rendering
      ...{
        'x-resourceful-autocreated':
          ('autoCreate' in definition && definition.autoCreate === true) ||
          ('autoCreate' in dataType && dataType.autoCreate === true) ||
          ('isPrimary' in definition && definition.isPrimary === true) ||
          false,
        'x-resourceful-autoupdated':
          ('autoUpdate' in definition && definition.autoUpdate === true) ||
          ('autoUpdate' in dataType && dataType.autoUpdate === true) ||
          false,
        'x-resourceful-immutable':
          ('immutable' in definition && definition.immutable === true) || false,
        'x-resourceful-primary-key':
          ('isPrimary' in definition && definition.isPrimary === true) || false,
        'x-resourceful-relationship': relationshipMeta,
      },
      ...stripUndefinedValuesFromObject({
        'x-resourceful-choices': this.#resolveChoices(definition.enum, dataType.enum),
      }),
    })

    return schemaProperties as ResourcefulModelOpenApiSchema
  }

  /**
   * Generates OpenAPI schema for a computed accessor property.
   *
   * This method converts resourceful computed accessor metadata into an OpenAPI
   * schema object, applying the same data type extraction improvements as column
   * schema generation while maintaining all existing functionality.
   *
   * @param propertyKey - The name of the computed accessor property
   * @param propertyMeta - The resourceful metadata for the computed accessor
   * @returns OpenAPI schema object for the computed accessor
   *
   * @example
   * ```typescript
   * const service = new OpenApiSchemaService()
   * const schema = service.generateComputedAccessorSchema('fullName', accessorMeta)
   * // Returns: { type: 'string', readOnly: true, ... }
   * ```
   */
  generateComputedAccessorSchema(
    propertyKey: string,
    propertyMeta: ResourcefulModelComputedAccessorMetaSchema
  ): ResourcefulModelOpenApiSchema {
    if (!isObject(propertyMeta) || !propertyMeta.definition || !propertyMeta.lucidDefinitions) {
      return {}
    }
    // Extract data type information from the resourceful data type
    const dataTypeInfo = this.#extractDataTypeInfo(propertyMeta.definition.type)

    // Get the field key (handles serializeAs)
    const fieldKey = this.#getFieldKey(
      propertyKey,
      propertyMeta.lucidDefinitions as LucidDefinitionWithSerializeAs
    )

    // Extract validation constraints from the data type instance and definition
    const dataType = propertyMeta.definition.type as DataTypeWithConstraints
    const definition = propertyMeta.definition as PropertyDefinitionWithConstraints

    // Build the schema object with proper type information and all existing properties
    /* istanbul ignore next -- @preserve */
    const schemaProperties = stripUndefinedValuesFromObject({
      // Core OpenAPI type information (properly extracted)
      type: dataTypeInfo.type,
      format: dataTypeInfo.format,

      // Schema metadata
      items: definition.items || dataType.items,
      title: fieldKey || propertyKey,
      description: definition.description,
      default:
        'default' in definition
          ? definition.default
          : this.getDefaultValueFromPropertyMeta(propertyMeta),

      // Numeric constraints (from data type or definition)
      multipleOf: definition.multipleOf || dataType.multipleOf,
      maximum: definition.maximum || dataType.maximum,
      exclusiveMaximum: definition.exclusiveMaximum || dataType.exclusiveMaximum,
      minimum: definition.minimum || dataType.minimum,
      exclusiveMinimum: definition.exclusiveMinimum || dataType.exclusiveMinimum,

      // String constraints (from data type or definition)
      maxLength: definition.maxLength || dataType.maxLength,
      minLength: definition.minLength || dataType.minLength,
      pattern: definition.pattern || dataType.pattern,

      // Object constraints (from data type or definition)
      additionalProperties: definition.additionalProperties || dataType.additionalProperties,
      maxProperties: definition.maxProperties || dataType.maxProperties,
      minProperties: definition.minProperties || dataType.minProperties,
      properties: definition.properties || dataType.properties,

      // Array constraints (from data type or definition)
      maxItems: definition.maxItems || dataType.maxItems,
      minItems: definition.minItems || dataType.minItems,
      uniqueItems: definition.uniqueItems || dataType.uniqueItems,

      // Schema composition (from data type or definition)
      allOf: definition.allOf || dataType.allOf,
      oneOf: definition.oneOf || dataType.oneOf,
      anyOf: definition.anyOf || dataType.anyOf,
      not: definition.not || dataType.not,

      // Validation and constraints (from data type or definition)
      required: definition.required || dataType.required,
      enum: this.#resolveEnums(definition.enum, dataType.enum),
      nullable:
        definition.nullable !== undefined ? definition.nullable : dataType.nullable || false,

      // Access control
      readOnly: propertyMeta.canRead && !propertyMeta.canWrite,
      writeOnly: !propertyMeta.canRead && propertyMeta.canWrite,

      // Documentation
      externalDocs: definition.externalDocs,
      example: definition.example,

      // Form Rendering
      ...{
        'x-resourceful-autocreated': !propertyMeta.canWrite,
        'x-resourceful-autoupdated': !propertyMeta.canWrite,
        'x-resourceful-immutable': !propertyMeta.canWrite,
        'x-resourceful-primary-key': false,
        'x-resourceful-relationship': false,
      },
      ...stripUndefinedValuesFromObject({
        'x-resourceful-choices': this.#resolveChoices(definition.enum, dataType.enum),
      }),
    })

    return schemaProperties as ResourcefulModelOpenApiSchema
  }

  /**
   * Generates OpenAPI schema for a relationship property.
   *
   * This method converts resourceful relationship metadata into an OpenAPI
   * reference object, preserving existing relationship reference generation
   * logic and ensuring proper handling of related model references.
   *
   * @param propertyKey - The name of the relationship property
   * @param propertyMeta - The resourceful metadata for the relationship
   * @returns OpenAPI reference object for the relationship, or undefined if not applicable
   *
   * @example
   * ```typescript
   * const service = new OpenApiSchemaService()
   * const schema = service.generateRelationshipSchema('user', relationshipMeta)
   * // Returns: { $ref: '#/components/schemas/User' }
   * ```
   */
  async generateRelationshipSchema(
    _propertyKey: string,
    propertyMeta: ResourcefulModelRelationshipMetaSchema,
    operation: 'read' | 'write',
    options: NormalizedGenerateModelSchemaOptions
  ): Promise<ResourcefulModelOpenApiRelatedSchema | ResourcefulModelOpenApiSchema | undefined> {
    if (
      !isObject(propertyMeta) ||
      !propertyMeta.definition ||
      !propertyMeta.lucidDefinitions ||
      !propertyMeta.relatedModel
    ) {
      return undefined
    }
    const relatedModel = propertyMeta.relatedModel()
    /* istanbul ignore if -- @preserve */
    if (!relatedModel) {
      // If the related model is not defined, we cannot generate a schema for it
      return undefined
    }
    /* istanbul ignore if -- @preserve */
    if (!isResourcefulModel(relatedModel)) {
      // If the related model is not a ResourcefulModel, we cannot generate a schema for it
      return undefined
    }

    if (options.includeRelations && options.relationsDeepLevel === 0) {
      return undefined
    }

    if (options.includeRelations) {
      if (!options.app || !options.ctx) {
        //TODO: In expected environment, this should never happen. Should be tested and removed later.
        return undefined
      }

      const updatedOptions = {
        ...options,
        relationsDeepLevel:
          options.relationsDeepLevel === null ? null : options.relationsDeepLevel - 1,
      }

      return relatedModel.$asOpenApiSchemaObject(
        options.ctx,
        options.app,
        operation,
        updatedOptions
      )
    }

    const ref: string = `#/components/schemas/${relatedModel.$resourcefulName}`
    return {
      '$ref': ref,
      'x-resourceful-display-key': resolveModelDisplayColumn(relatedModel),
    }
  }

  /**
   * Extracts default value from property metadata using Joi description.
   *
   * This protected method provides access to the default value extraction logic
   * that was previously part of the mixin. It examines the Joi validator
   * description to find default values specified in the schema.
   *
   * @param propertyMeta - The resourceful metadata containing the validator
   * @returns The default value if found, undefined otherwise
   *
   * @example
   * ```typescript
   * const service = new OpenApiSchemaService()
   * const defaultValue = service.getDefaultValueFromPropertyMeta(propertyMeta)
   * ```
   */
  protected getDefaultValueFromPropertyMeta(
    propertyMeta: ResourcefulModelColumnMetaSchema | ResourcefulModelComputedAccessorMetaSchema
  ): unknown | undefined {
    const joiDescription = propertyMeta.validator.describe()
    /* istanbul ignore if -- @preserve */
    if (
      isObject(joiDescription) &&
      'flags' in joiDescription &&
      isObject(joiDescription.flags) &&
      'default' in joiDescription.flags
    ) {
      return joiDescription.flags.default
    }
    /* istanbul ignore next -- @preserve */
    return undefined
  }
}
